import * as React from "react";
import { Column } from "react-table";
import { isActiveCareCoordinator } from "intake_shared/src/assignment";
import { AclContext } from "../../../contexts/AclContext";
import {
    addCoordinatorTimeOff,
    deleteCoordinatorTimeOff,
    getCoordinatorTimeOff,
    TimeOffRange,
} from "../../../store/assignment";
import Alert from "../bits/Alert/Alert";
import { AlertDialog } from "../bits/AlertDialog/AlertDialog";
import { Button } from "../bits/Button/Button";
import { Card } from "../bits/Card/Card";
import { DatePicker } from "../bits/DatePicker/DatePicker";
import { Dialog } from "../bits/Dialog/Dialog";
import EmptyListState from "../bits/EmptyListState/EmptyListState";
import { Input } from "../bits/Input/Input";
import Table from "../bits/Table/Table";
import TrashIcon from "../icons/Trash";
import styles from "./TimeOff.module.scss";
import { validTimeOffRange } from "./assignmentUtils";

function calendarDay(date: Date | null): string {
    if (!date || !Number.isFinite(date.getTime())) return "";
    return [
        String(date.getFullYear()).padStart(4, "0"),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");
}

function localDay(day: string): Date | null {
    if (!validTimeOffRange(day, day)) return null;
    const [year, month, date] = day.split("-").map(Number);
    const result = new Date(0);
    result.setFullYear(year, month - 1, date);
    result.setHours(12, 0, 0, 0);
    return result;
}

function displayDay(day: string): string {
    const date = localDay(day);
    return date
        ? new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
          }).format(date)
        : day;
}

function CalendarPopover({ children }: { children?: React.ReactNode }) {
    return <div className="v2-form">{children}</div>;
}

export default function TimeOffCard({
    email,
    onChanged,
    onOverlayChange,
}: {
    email: string;
    onChanged?: (ranges: TimeOffRange[]) => void;
    onOverlayChange?: (open: boolean) => void;
}) {
    const context = React.useContext(AclContext);
    const actor = context?.authUser.user?.email?.trim().toLowerCase() || "";
    const account = context?.authUser;
    const activeAdmin =
        account?.acl?.super_admin === true &&
        !account.deleted &&
        !account.acl.blocked;
    const canManage = Boolean(
        actor &&
            (activeAdmin ||
                (actor === email.trim().toLowerCase() &&
                    isActiveCareCoordinator(account)))
    );
    const [ranges, setRanges] = React.useState<TimeOffRange[]>([]);
    const [start, setStart] = React.useState("");
    const [end, setEnd] = React.useState("");
    const [note, setNote] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState("");
    const [formError, setFormError] = React.useState("");
    const [showAdd, setShowAdd] = React.useState(false);
    const [deleting, setDeleting] = React.useState<TimeOffRange | null>(null);
    const addButton = React.useRef<HTMLButtonElement>(null);
    const reloadButton = React.useRef<HTMLButtonElement>(null);
    const deleteDescription = React.useRef<HTMLDivElement>(null);
    const [restoreAddFocus, setRestoreAddFocus] = React.useState(false);
    const formId = `time-off-form-${email}`;
    const loadVersion = React.useRef(0);
    React.useEffect(() => {
        onOverlayChange?.(showAdd || Boolean(deleting) || saving);
    }, [showAdd, deleting, saving, onOverlayChange]);
    React.useEffect(() => () => onOverlayChange?.(false), [onOverlayChange]);
    React.useLayoutEffect(() => {
        if (saving && deleting) {
            deleteDescription.current
                ?.closest<HTMLElement>('[role="alertdialog"]')
                ?.focus();
        }
    }, [saving, deleting]);
    const load = React.useCallback(
        async (server = false) => {
            const version = ++loadVersion.current;
            setLoading(true);
            setError("");
            try {
                const result = server
                    ? await getCoordinatorTimeOff(email, "server")
                    : await getCoordinatorTimeOff(email);
                if (version !== loadVersion.current) return undefined;
                setRanges(result);
                return result;
            } catch {
                if (version !== loadVersion.current) return;
                setError("Could not load time off. Try again.");
            } finally {
                if (version === loadVersion.current) setLoading(false);
            }
        },
        [email]
    );
    React.useEffect(() => {
        const version = loadVersion;
        void load();
        return () => {
            version.current++;
        };
    }, [load]);

    React.useEffect(() => {
        if (restoreAddFocus && !saving && !loading && !showAdd && !deleting) {
            (error ? reloadButton.current : addButton.current)?.focus();
            setRestoreAddFocus(false);
        }
    }, [restoreAddFocus, saving, loading, showAdd, deleting, error]);

    const closeAdd = () => {
        if (saving) return;
        setShowAdd(false);
        setRestoreAddFocus(true);
    };
    const addRange = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canManage || saving) return;
        if (!validTimeOffRange(start, end) || note.length > 200) {
            setFormError(
                "Enter valid dates with the end on or after the start, and a note of at most 200 characters."
            );
            return;
        }
        setSaving(true);
        setFormError("");
        try {
            await addCoordinatorTimeOff(email, actor, { start, end, note });
            const result = await load(true);
            if (result) onChanged?.(result);
            setShowAdd(false);
            setRestoreAddFocus(true);
            setStart("");
            setEnd("");
            setNote("");
        } catch {
            setFormError("Could not add time off. Try again.");
        } finally {
            setSaving(false);
        }
    };
    const reloadRanges = async () => {
        const result = await load(true);
        if (result) onChanged?.(result);
        setRestoreAddFocus(true);
    };
    const removeRange = async () => {
        if (!canManage || saving || !deleting) return;
        setSaving(true);
        setError("");
        try {
            await deleteCoordinatorTimeOff(email, deleting.id);
            const result = await load(true);
            if (result) onChanged?.(result);
            setDeleting(null);
            setRestoreAddFocus(true);
        } catch {
            setError("Could not delete time off. Try again.");
        } finally {
            setSaving(false);
        }
    };
    const columns = React.useMemo<Column<TimeOffRange>[]>(
        () => [
            {
                Header: "First day",
                accessor: "start",
                Cell: ({ value }) => <span>{displayDay(value)}</span>,
            },
            {
                Header: "Last day",
                accessor: "end",
                Cell: ({ value }) => <span>{displayDay(value)}</span>,
            },
            {
                Header: "Note",
                accessor: "note",
                Cell: ({ value }) => (
                    <span className={styles.note}>{value || "—"}</span>
                ),
            },
            ...(canManage
                ? [
                      {
                          id: "actions",
                          Header: "",
                          Cell: ({ row }: { row: { original: TimeOffRange } }) => (
                              <Button
                                  variant="normal"
                                  size="icon"
                                  disabled={saving || Boolean(error)}
                                  onClick={() => {
                                      onOverlayChange?.(true);
                                      setError("");
                                      setDeleting(row.original);
                                  }}
                                  aria-label={`Delete time off ${row.original.start} to ${row.original.end}`}
                                  title="Delete time off"
                              >
                                  <TrashIcon stroke="var(--color-gray)" />
                              </Button>
                          ),
                      },
                  ]
                : []),
        ],
        [canManage, saving, error, onOverlayChange]
    );

    return (
        <section aria-label={`Time off for ${email}`}>
            <Card classNames={styles.card}>
                <div className={styles.header}>
                    <div className={styles.heading}>
                        <h2>Scheduled time off</h2>
                        <p>Whole days in Eastern time. Both dates are included.</p>
                    </div>
                    {canManage && (
                        <Button
                            ref={addButton}
                            disabled={loading || saving || Boolean(error)}
                            onClick={() => {
                                onOverlayChange?.(true);
                                setFormError("");
                                setShowAdd(true);
                            }}
                        >
                            Add time off
                        </Button>
                    )}
                </div>
                {error && !deleting && (
                    <Alert type="danger">
                        <p>{error}</p>
                    </Alert>
                )}
                {loading ? (
                    <div className={styles.loading} role="status">
                        Loading time off…
                    </div>
                ) : ranges.length > 0 ? (
                    <Table columns={columns} data={ranges} allowSelect={false} />
                ) : (
                    !error && (
                        <EmptyListState
                            emptyMessage="No time off scheduled"
                            stateHelperText="Add the dates you will be away so new inquiries can go to an available coordinator."
                            cardClass={styles.emptyCard}
                            wrapperClass={styles.emptyState}
                        />
                    )
                )}
                {error && !deleting && (
                    <Button
                        variant="secondary"
                        ref={reloadButton}
                        disabled={saving}
                        onClick={reloadRanges}
                    >
                        Reload time off
                    </Button>
                )}
                <div className={styles.help}>
                    <p>
                        Applies to new inquiries while weighted assignment is on.
                        Returning clients keep their coordinator. If everyone is
                        unavailable, assignments are flagged for staff.
                    </p>
                    <p>
                        Block the same dates in SimplyBook separately to update
                        booking availability.
                    </p>
                </div>
            </Card>
            <Dialog
                open={showAdd}
                onClose={closeAdd}
                onCancel={closeAdd}
                title="Add time off"
                ariaLabel={`Add time off for ${email}`}
                focusManagement={{ portalId: `time-off-calendar-${email}` }}
                saveText={saving ? "Saving…" : "Add time off"}
                submitBtnFormValue={formId}
                isSubmitBtnDisabled={saving}
                isCancelBtnDisabled={saving}
                wrapperClassName={styles.dialog}
            >
                <form id={formId} onSubmit={addRange} className={styles.form}>
                    <p className={styles.formHelp}>
                        Choose the first and last day you will be away, in Eastern
                        time.
                    </p>
                    {formError && (
                        <Alert type="danger">
                            <p>{formError}</p>
                        </Alert>
                    )}
                    <div className={styles.dates}>
                        <DatePicker
                            label="First day off"
                            id={`time-off-start-${email}`}
                            selected={localDay(start)}
                            onChangeRaw={() => setStart("")}
                            onChange={(date: Date | null) =>
                                setStart(calendarDay(date))
                            }
                            portalId={`time-off-calendar-${email}`}
                            popperContainer={CalendarPopover}
                            popperClassName={styles.calendarPopper}
                            popperProps={{ positionFixed: true }}
                            popperModifiers={{
                                preventOverflow: {
                                    enabled: true,
                                    boundariesElement: "viewport",
                                    padding: 8,
                                },
                            }}
                            disabled={saving}
                            required
                            isRequired
                            autoFocus
                        />
                        <DatePicker
                            label="Last day off"
                            id={`time-off-end-${email}`}
                            selected={localDay(end)}
                            onChangeRaw={() => setEnd("")}
                            minDate={localDay(start) || undefined}
                            onChange={(date: Date | null) =>
                                setEnd(calendarDay(date))
                            }
                            portalId={`time-off-calendar-${email}`}
                            popperContainer={CalendarPopover}
                            popperClassName={styles.calendarPopper}
                            popperProps={{ positionFixed: true }}
                            popperModifiers={{
                                preventOverflow: {
                                    enabled: true,
                                    boundariesElement: "viewport",
                                    padding: 8,
                                },
                            }}
                            disabled={saving}
                            required
                            isRequired
                        />
                    </div>
                    <Input
                        label="Note"
                        isOptional
                        name={`time-off-note-${email}`}
                        id={`time-off-note-${email}`}
                        value={note}
                        maxLength={200}
                        placeholder="For example, vacation"
                        onChange={(event) => setNote(event.target.value)}
                        disabled={saving}
                    />
                </form>
            </Dialog>
            <AlertDialog
                open={Boolean(deleting)}
                onClose={() => {
                    if (!saving) {
                        setDeleting(null);
                        setError("");
                    }
                }}
                title="Delete time off"
                description={
                    <div className={styles.confirmation} ref={deleteDescription}>
                        <p>
                            Remove time off from{" "}
                            {deleting && displayDay(deleting.start)} to{" "}
                            {deleting && displayDay(deleting.end)}?
                        </p>
                        {error && (
                            <Alert type="danger">
                                <p>{error}</p>
                            </Alert>
                        )}
                    </div>
                }
                onSaveClick={removeRange}
                saveText={saving ? "Deleting…" : "Delete"}
                isDeleting
                isSaveBtnDisabled={saving}
                isCancelBtnDisabled={saving}
            />
        </section>
    );
}
